import React, { useState, useEffect, useRef, useReducer } from 'react';
import { debounce } from 'lodash';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { Calendar, CreditCard, DollarSign, TrendingUp, Bell, Plus, User, Home, RefreshCw, AlertCircle, Phone, MessageSquare, CheckCircle, Search, Filter, Eye, Trash2, Clock, Bot, Users, Activity, Download, FileText, ArrowLeft } from 'lucide-react';

// Firebase imports - usando tus datos reales
import { collection, getDocs, query, orderBy, limit, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Importar el componente UserInfo
import UserInfo from './UserInfo';

// Reducer para el estado del dashboard
const dashboardReducer = (state, action) => {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_DATA':
            return { ...state, ...action.payload, loading: false };
        case 'UPDATE_COLLECTION':
            return { ...state, [action.collection]: action.data };
        case 'SET_REALTIME_STATUS':
            return { ...state, realTimeConnected: action.payload };
        case 'SET_FILTER':
            return { ...state, filters: { ...state.filters, ...action.payload } };
        case 'SET_SELECTED_USER':
            return { ...state, selectedUser: action.payload, currentView: action.payload ? 'user-detail' : state.currentView };
        case 'SET_VIEW':
            return { ...state, currentView: action.payload, selectedUser: action.payload === 'user-detail' ? state.selectedUser : null };
        case 'SET_ERROR':
            return { ...state, error: action.payload, loading: false };
        default:
            return state;
    }
};

const initialState = {
    loading: true,
    interactions: [],
    responses: [],
    users: [],
    expenses: [],
    reminders: [],
    subscriptions: [],
    appointments: [],
    selectedUser: null,
    currentView: 'overview',
    error: null,
    realTimeConnected: false,
    filters: {
        dateRange: 'today',
        messageType: 'all',
        userId: 'all'
    }
};

const AdminDashboard = () => {
    const [state, dispatch] = useReducer(dashboardReducer, initialState);
    const [searchTerm, setSearchTerm] = useState('');
    const unsubscribeFunctions = useRef([]); // Para guardar las funciones de desuscripción

    // Configurar listeners en tiempo real para Firebase
    const setupRealTimeListeners = () => {
        dispatch({ type: 'SET_LOADING', payload: true });

        try {
            console.log('🔥 Configurando listeners en tiempo real...');

            // Limpiar listeners anteriores
            unsubscribeFunctions.current.forEach(unsub => unsub());
            unsubscribeFunctions.current = [];

            // 1. Listener para interacciones
            const interactionsQuery = query(
                collection(db, 'interactions'),
                orderBy('timestamp', 'desc'),
                limit(200)
            );

            const unsubInteractions = onSnapshot(interactionsQuery, (snapshot) => {
                const interactions = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    timestamp: doc.data().timestamp?.toDate() || new Date(),
                    timestampMST: doc.data().timestampMST || new Date().toISOString()
                }));

                dispatch({
                    type: 'UPDATE_COLLECTION',
                    collection: 'interactions',
                    data: interactions
                });

                console.log('🔄 Interacciones actualizadas:', interactions.length);
            }, (error) => {
                console.error('Error en listener de interacciones:', error);
            });

            // 2. Listener para respuestas del asistente
            const responsesQuery = query(
                collection(db, 'assistant_responses'),
                orderBy('timestamp', 'desc'),
                limit(200)
            );

            const unsubResponses = onSnapshot(responsesQuery, (snapshot) => {
                const responses = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    timestamp: doc.data().timestamp?.toDate() || new Date()
                }));

                dispatch({
                    type: 'UPDATE_COLLECTION',
                    collection: 'responses',
                    data: responses
                });

                console.log('🔄 Respuestas actualizadas:', responses.length);
            });

            // 3. Listener para usuarios
            const unsubUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
                const users = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        ...data,
                        phone: data.userId || doc.id,
                        firstInteraction: data.firstInteraction?.toDate() || new Date(),
                        lastInteraction: data.lastInteraction?.toDate() || new Date(),
                        totalMessages: data.stats?.totalMessages || 0,
                        totalExpenses: 0,
                        totalReminders: data.stats?.remindersCreated || 0
                    };
                });

                dispatch({
                    type: 'UPDATE_COLLECTION',
                    collection: 'users',
                    data: users
                });

                console.log('🔄 Usuarios actualizados:', users.length);
            });

            // 4. Listener para gastos
            const expensesQuery = query(
                collection(db, 'expenses'),
                orderBy('createdAt', 'desc'),
                limit(100)
            );

            const unsubExpenses = onSnapshot(expensesQuery, (snapshot) => {
                const expenses = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt?.toDate() || new Date(),
                    date: doc.data().date || new Date().toISOString()
                }));

                dispatch({
                    type: 'UPDATE_COLLECTION',
                    collection: 'expenses',
                    data: expenses
                });

                console.log('🔄 Gastos actualizados:', expenses.length);
            });

            // 5. Listener para recordatorios
            const remindersQuery = query(
                collection(db, 'reminders'),
                orderBy('createdAt', 'desc'),
                limit(100)
            );

            const unsubReminders = onSnapshot(remindersQuery, (snapshot) => {
                const reminders = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt?.toDate() || new Date(),
                    date: doc.data().date || new Date().toISOString()
                }));

                dispatch({
                    type: 'UPDATE_COLLECTION',
                    collection: 'reminders',
                    data: reminders
                });

                console.log('🔄 Recordatorios actualizados:', reminders.length);
            });

            // 6. Listener para suscripciones
            const unsubSubscriptions = onSnapshot(collection(db, 'subscriptions'), (snapshot) => {
                const subscriptions = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt?.toDate() || new Date()
                }));

                dispatch({
                    type: 'UPDATE_COLLECTION',
                    collection: 'subscriptions',
                    data: subscriptions
                });

                console.log('🔄 Suscripciones actualizadas:', subscriptions.length);
            });

            // 7. Listener para citas
            const unsubAppointments = onSnapshot(collection(db, 'appointments'), (snapshot) => {
                const appointments = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt?.toDate() || new Date()
                }));

                dispatch({
                    type: 'UPDATE_COLLECTION',
                    collection: 'appointments',
                    data: appointments
                });

                console.log('🔄 Citas actualizadas:', appointments.length);
            });

            // Guardar todas las funciones de desuscripción
            unsubscribeFunctions.current = [
                unsubInteractions,
                unsubResponses,
                unsubUsers,
                unsubExpenses,
                unsubReminders,
                unsubSubscriptions,
                unsubAppointments
            ];

            // Marcar como conectado y quitar loading después de un momento
            setTimeout(() => {
                dispatch({ type: 'SET_LOADING', payload: false });
                dispatch({ type: 'SET_REALTIME_STATUS', payload: true });
                console.log('✅ Todos los listeners configurados correctamente');
            }, 2000);

        } catch (error) {
            console.error('❌ Error configurando listeners:', error);
            dispatch({
                type: 'SET_ERROR',
                payload: 'Error configurando tiempo real: ' + error.message
            });
        }
    };

    useEffect(() => {
        setupRealTimeListeners();

        // Cleanup al desmontar
        return () => {
            console.log('🧹 Limpiando listeners...');
            unsubscribeFunctions.current.forEach(unsub => unsub());
        };
    }, []);

    // Búsqueda con debounce
    const debouncedSearch = debounce((term) => {
        setSearchTerm(term);
    }, 300);

    // Filtrar datos según criterios
    const getFilteredData = () => {
        let filtered = state.interactions;

        // Filtro por rango de fecha
        if (state.filters.dateRange !== 'all') {
            const now = new Date();
            const startDate = new Date();

            switch (state.filters.dateRange) {
                case 'today':
                    startDate.setHours(0, 0, 0, 0);
                    break;
                case 'week':
                    startDate.setDate(now.getDate() - 7);
                    break;
                case 'month':
                    startDate.setMonth(now.getMonth() - 1);
                    break;
            }

            filtered = filtered.filter(item => item.timestamp >= startDate);
        }

        // Filtro por tipo de mensaje
        if (state.filters.messageType !== 'all') {
            filtered = filtered.filter(item => item.messageType === state.filters.messageType);
        }

        // Filtro por usuario
        if (state.filters.userId !== 'all') {
            filtered = filtered.filter(item => item.userId === state.filters.userId);
        }

        // Búsqueda de texto
        if (searchTerm) {
            filtered = filtered.filter(item =>
                item.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.userId.includes(searchTerm)
            );
        }

        return filtered;
    };

    // Calcular estadísticas REALES
    const getStats = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const todayInteractions = state.interactions.filter(item =>
            item.timestamp >= today
        );

        const audioMessages = state.interactions.filter(item =>
            item.messageType === 'audio'
        );

        const totalExpenses = state.expenses.reduce((sum, expense) =>
            sum + (expense.amount || 0), 0
        );

        const activeSubscriptions = state.subscriptions.filter(sub =>
            sub.isActive !== false
        );

        const pendingReminders = state.reminders.filter(reminder =>
            reminder.status === 'pending'
        );

        return {
            totalUsers: state.users.length,
            totalInteractions: state.interactions.length,
            todayInteractions: todayInteractions.length,
            audioMessages: audioMessages.length,
            responseRate: state.interactions.length > 0 ?
                Math.round((state.responses.length / state.interactions.length) * 100) : 0,
            totalExpenses: totalExpenses,
            totalExpenseCount: state.expenses.length,
            activeSubscriptions: activeSubscriptions.length,
            pendingReminders: pendingReminders.length,
            totalAppointments: state.appointments.length
        };
    };

    // Datos para gráficos
    const getChartData = () => {
        // Interacciones por hora del día
        const hourlyData = Array.from({ length: 24 }, (_, hour) => ({
            hour: `${hour}:00`,
            interactions: state.interactions.filter(item =>
                new Date(item.timestamp).getHours() === hour
            ).length
        }));

        // Tipos de mensaje
        const messageTypeData = [
            { name: 'Texto', value: state.interactions.filter(i => i.messageType === 'text').length, color: '#3B82F6' },
            { name: 'Audio', value: state.interactions.filter(i => i.messageType === 'audio').length, color: '#10B981' },
            { name: 'Imagen', value: state.interactions.filter(i => i.messageType === 'image').length, color: '#F59E0B' }
        ];

        return { hourlyData, messageTypeData };
    };

    const stats = getStats();
    const filteredInteractions = getFilteredData();
    const { hourlyData, messageTypeData } = getChartData();

    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleString('es-MX', {
            timeZone: 'America/Phoenix',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatPhone = (phone) => {
        if (!phone) return 'N/A';
        // Si ya tiene formato, devolverlo tal como está
        if (phone.includes(' ') || phone.includes('-')) return phone;
        // Si es solo números, formatear
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length >= 10) {
            return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})/, '+$1 $2 $3 $4');
        }
        return phone;
    };

    const getUserResponse = (messageId) => {
        return state.responses.find(r => r.originalMessageId === messageId);
    };

    const renderOverview = () => (
        <div className="space-y-6">
            {/* Error state */}
            {state.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                        <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                        <span className="text-red-800">{state.error}</span>
                    </div>
                </div>
            )}

            {/* KPIs Expandidos */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4">
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <Users className="h-8 w-8 text-blue-500" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Usuarios</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <MessageSquare className="h-8 w-8 text-green-500" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Mensajes</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.totalInteractions}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <Activity className="h-8 w-8 text-orange-500" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Hoy</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.todayInteractions}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <Phone className="h-8 w-8 text-purple-500" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Audios</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.audioMessages}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <DollarSign className="h-8 w-8 text-green-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Gastos ($)</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.totalExpenses.toLocaleString()}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <CreditCard className="h-8 w-8 text-blue-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Suscripciones</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.activeSubscriptions}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <Bell className="h-8 w-8 text-yellow-500" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Recordatorios</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.pendingReminders}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <Bot className="h-8 w-8 text-indigo-500" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Respuesta %</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.responseRate}%</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Gráficos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Interacciones por Hora</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={hourlyData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="hour" />
                            <YAxis />
                            <Tooltip />
                            <Line type="monotone" dataKey="interactions" stroke="#3B82F6" strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Tipos de Mensaje</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={messageTypeData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {messageTypeData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Tabla de actividad reciente */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Actividad Reciente</h3>
                </div>
                <div className="divide-y divide-gray-200">
                    {state.interactions.slice(0, 5).map((interaction) => (
                        <div key={interaction.id} className="p-4 flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className={`w-2 h-2 rounded-full ${interaction.messageType === 'audio' ? 'bg-green-500' : 'bg-blue-500'
                                    }`}></div>
                                <span className="text-sm font-medium">{formatPhone(interaction.userId)}</span>
                                <span className="text-sm text-gray-500 max-w-xs truncate">
                                    {interaction.message}
                                </span>
                            </div>
                            <span className="text-xs text-gray-400">
                                {formatTime(interaction.timestamp)}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderInteractions = () => (
        <div className="space-y-6">
            {/* Filtros y búsqueda */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Buscar mensajes o números de teléfono..."
                                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                onChange={(e) => debouncedSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <select
                        value={state.filters.dateRange}
                        onChange={(e) => dispatch({ type: 'SET_FILTER', payload: { dateRange: e.target.value } })}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="today">Hoy</option>
                        <option value="week">Última semana</option>
                        <option value="month">Último mes</option>
                        <option value="all">Todo</option>
                    </select>

                    <select
                        value={state.filters.messageType}
                        onChange={(e) => dispatch({ type: 'SET_FILTER', payload: { messageType: e.target.value } })}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">Todos los tipos</option>
                        <option value="text">Texto</option>
                        <option value="audio">Audio</option>
                        <option value="image">Imagen</option>
                    </select>

                    <button
                        onClick={setupRealTimeListeners}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                        <RefreshCw className="h-4 w-4" />
                        Reconectar
                    </button>
                </div>
            </div>

            {/* Lista de interacciones */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Conversaciones ({filteredInteractions.length})
                    </h3>
                </div>

                <div className="divide-y divide-gray-200">
                    {filteredInteractions.map((interaction) => {
                        const response = getUserResponse(interaction.id);
                        return (
                            <div key={interaction.id} className="p-6 hover:bg-gray-50">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="flex items-center gap-2">
                                                <Phone className="h-4 w-4 text-gray-400" />
                                                <span className="font-medium text-gray-900">
                                                    {formatPhone(interaction.userId)}
                                                </span>
                                            </div>

                                            <span className={`px-2 py-1 text-xs rounded-full ${interaction.messageType === 'audio'
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-blue-100 text-blue-800'
                                                }`}>
                                                {interaction.messageType === 'audio' ? '🎤 Audio' : '💬 Texto'}
                                            </span>

                                            <span className="text-sm text-gray-500">
                                                {formatTime(interaction.timestamp)}
                                            </span>
                                        </div>

                                        <div className="mb-3">
                                            <p className="text-gray-900 bg-gray-100 p-3 rounded-lg">
                                                "{interaction.message}"
                                            </p>
                                        </div>

                                        {response && (
                                            <div className="mt-3">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Bot className="h-4 w-4 text-blue-500" />
                                                    <span className="text-sm font-medium text-blue-700">Respuesta del Bot:</span>
                                                    <span className="text-xs text-gray-500">
                                                        {formatTime(response.timestamp)}
                                                    </span>
                                                </div>
                                                <p className="text-blue-900 bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                                                    {response.response}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2 ml-4">
                                        {response ? (
                                            <CheckCircle className="h-5 w-5 text-green-500" />
                                        ) : (
                                            <AlertCircle className="h-5 w-5 text-red-500" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {filteredInteractions.length === 0 && (
                        <div className="p-12 text-center">
                            <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No hay conversaciones</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                No se encontraron conversaciones con los filtros aplicados.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderUsers = () => (
        <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Usuarios Registrados ({state.users.length})
                    </h3>
                </div>

                <div className="divide-y divide-gray-200">
                    {state.users.map((user) => {
                        // Calcular gastos del usuario
                        const userExpenses = state.expenses.filter(exp => exp.userId === user.id);
                        const totalUserExpenses = userExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);

                        return (
                            <div key={user.id} className="p-6 hover:bg-gray-50">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div className="flex-shrink-0">
                                            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                <User className="h-5 w-5 text-blue-600" />
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-3">
                                                <h4 className="text-sm font-medium text-gray-900">
                                                    {formatPhone(user.phone)}
                                                </h4>
                                                <span className="text-xs text-gray-500">ID: {user.id}</span>
                                            </div>
                                            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                                <span>Primera: {formatTime(user.firstInteraction)}</span>
                                                <span>Última: {formatTime(user.lastInteraction)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-6 text-sm">
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-gray-900">{user.totalMessages}</div>
                                            <div className="text-gray-500">Mensajes</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-green-600">${totalUserExpenses.toFixed(0)}</div>
                                            <div className="text-gray-500">Gastos</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-blue-600">{user.totalReminders}</div>
                                            <div className="text-gray-500">Recordatorios</div>
                                        </div>
                                        <button
                                            onClick={() => dispatch({ type: 'SET_SELECTED_USER', payload: user.id })}
                                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
                                        >
                                            <Eye className="h-4 w-4" />
                                            Ver Perfil
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    const renderExpenses = () => (
        <div className="space-y-6">
            {/* Resumen de gastos */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <DollarSign className="h-8 w-8 text-green-500" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Gastos</p>
                            <p className="text-2xl font-bold text-gray-900">${stats.totalExpenses.toLocaleString()}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <FileText className="h-8 w-8 text-blue-500" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Cantidad</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.totalExpenseCount}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <TrendingUp className="h-8 w-8 text-purple-500" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Promedio</p>
                            <p className="text-2xl font-bold text-gray-900">
                                ${stats.totalExpenseCount > 0 ? (stats.totalExpenses / stats.totalExpenseCount).toFixed(0) : 0}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <Calendar className="h-8 w-8 text-orange-500" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Este Mes</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {state.expenses.filter(exp => {
                                    const expDate = new Date(exp.date || exp.createdAt);
                                    const currentMonth = new Date().getMonth();
                                    return expDate.getMonth() === currentMonth;
                                }).length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Lista de gastos */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Gastos Recientes ({state.expenses.length})
                    </h3>
                </div>

                <div className="divide-y divide-gray-200">
                    {state.expenses.slice(0, 20).map((expense) => (
                        <div key={expense.id} className="p-6 hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <div className="flex-shrink-0">
                                        <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                                            <span className="text-lg">{expense.categoryIcon || '💰'}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <h4 className="text-sm font-medium text-gray-900">
                                                {expense.description}
                                            </h4>
                                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                                {expense.categoryName || 'Sin categoría'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                            <span>Usuario: {formatPhone(expense.userId)}</span>
                                            <span>Fecha: {formatTime(expense.createdAt)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="text-right">
                                    <div className="text-2xl font-bold text-green-600">
                                        ${expense.amount?.toFixed(2) || '0.00'}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        {expense.location || 'Sin ubicación'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {state.expenses.length === 0 && (
                        <div className="p-12 text-center">
                            <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No hay gastos registrados</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                Los gastos aparecerán aquí cuando los usuarios los registren.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderAnalytics = () => {
        // Gastos por categoría
        const expensesByCategory = {};
        state.expenses.forEach(expense => {
            const category = expense.categoryName || 'Sin categoría';
            if (!expensesByCategory[category]) {
                expensesByCategory[category] = { total: 0, count: 0 };
            }
            expensesByCategory[category].total += expense.amount || 0;
            expensesByCategory[category].count += 1;
        });

        const categoryData = Object.entries(expensesByCategory).map(([name, data]) => ({
            name,
            value: data.total,
            count: data.count
        })).sort((a, b) => b.value - a.value);

        // Actividad por día de la semana
        const dayActivity = Array.from({ length: 7 }, (_, i) => {
            const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
            return {
                day: dayNames[i],
                messages: state.interactions.filter(interaction =>
                    new Date(interaction.timestamp).getDay() === i
                ).length
            };
        });

        // Gastos por mes (últimos 6 meses)
        const monthlyExpenses = Array.from({ length: 6 }, (_, i) => {
            const date = new Date();
            date.setMonth(date.getMonth() - i);
            const monthName = date.toLocaleDateString('es-MX', { month: 'short' });

            const monthExpenses = state.expenses.filter(expense => {
                const expenseDate = new Date(expense.date || expense.createdAt);
                return expenseDate.getMonth() === date.getMonth() &&
                    expenseDate.getFullYear() === date.getFullYear();
            });

            return {
                month: monthName,
                total: monthExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0),
                count: monthExpenses.length
            };
        }).reverse();

        return (
            <div className="space-y-6">
                {/* Gráficos de análisis */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Gastos por Categoría</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={categoryData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip formatter={(value) => [`${value.toLocaleString()}`, 'Monto']} />
                                <Bar dataKey="value" fill="#10B981" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Actividad por Día</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={dayActivity}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="day" />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="messages" fill="#3B82F6" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Gráfico de gastos mensuales */}
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Tendencia de Gastos (6 meses)</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={monthlyExpenses}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value) => [`${value.toLocaleString()}`, 'Total']} />
                            <Line type="monotone" dataKey="total" stroke="#10B981" strokeWidth={3} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Tabla de análisis detallado */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Análisis Detallado por Usuario</h3>
                    </div>

                    <div className="divide-y divide-gray-200">
                        {state.users.map((user) => {
                            const userInteractions = state.interactions.filter(i => i.userId === user.id);
                            const userExpenses = state.expenses.filter(e => e.userId === user.id);
                            const totalUserExpenses = userExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
                            const userReminders = state.reminders.filter(r => r.userId === user.id);
                            const userSubscriptions = state.subscriptions.filter(s => s.userId === user.id);

                            return (
                                <div key={user.id} className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                <User className="h-5 w-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-900">
                                                    {formatPhone(user.phone)}
                                                </h4>
                                                <p className="text-sm text-gray-500">
                                                    Último mensaje: {userInteractions.length > 0 ?
                                                        formatTime(Math.max(...userInteractions.map(i => i.timestamp))) : 'Nunca'
                                                    }
                                                </p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-5 gap-8 text-center">
                                            <div>
                                                <div className="text-lg font-bold text-blue-600">{userInteractions.length}</div>
                                                <div className="text-xs text-gray-500">Mensajes</div>
                                            </div>
                                            <div>
                                                <div className="text-lg font-bold text-green-600">${totalUserExpenses.toFixed(0)}</div>
                                                <div className="text-xs text-gray-500">Gastos</div>
                                            </div>
                                            <div>
                                                <div className="text-lg font-bold text-purple-600">{userExpenses.length}</div>
                                                <div className="text-xs text-gray-500">Transacciones</div>
                                            </div>
                                            <div>
                                                <div className="text-lg font-bold text-orange-600">{userReminders.length}</div>
                                                <div className="text-xs text-gray-500">Recordatorios</div>
                                            </div>
                                            <div>
                                                <div className="text-lg font-bold text-pink-600">{userSubscriptions.length}</div>
                                                <div className="text-xs text-gray-500">Suscripciones</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {state.users.length === 0 && (
                            <div className="p-12 text-center">
                                <Users className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay usuarios</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Los usuarios aparecerán aquí cuando interactúen con el bot.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Estadísticas rápidas */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Top Categorías</h4>
                        <div className="space-y-3">
                            {categoryData.slice(0, 5).map((category, index) => (
                                <div key={category.name} className="flex justify-between items-center">
                                    <div className="flex items-center">
                                        <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold mr-3">
                                            {index + 1}
                                        </span>
                                        <span className="text-sm font-medium">{category.name}</span>
                                    </div>
                                    <span className="text-sm text-gray-600">${category.value.toLocaleString()}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Usuarios Más Activos</h4>
                        <div className="space-y-3">
                            {state.users
                                .sort((a, b) => b.totalMessages - a.totalMessages)
                                .slice(0, 5)
                                .map((user, index) => (
                                    <div key={user.id} className="flex justify-between items-center">
                                        <div className="flex items-center">
                                            <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold mr-3">
                                                {index + 1}
                                            </span>
                                            <span className="text-sm font-medium">{formatPhone(user.phone)}</span>
                                        </div>
                                        <span className="text-sm text-gray-600">{user.totalMessages} msgs</span>
                                    </div>
                                ))}
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Resumen del Sistema</h4>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Total Usuarios:</span>
                                <span className="text-sm font-semibold">{stats.totalUsers}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Total Mensajes:</span>
                                <span className="text-sm font-semibold">{stats.totalInteractions}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Total Gastos:</span>
                                <span className="text-sm font-semibold">${stats.totalExpenses.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Suscripciones:</span>
                                <span className="text-sm font-semibold">{stats.activeSubscriptions}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Recordatorios:</span>
                                <span className="text-sm font-semibold">{stats.pendingReminders}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Tasa Respuesta:</span>
                                <span className="text-sm font-semibold">{stats.responseRate}%</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (state.loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Cargando datos desde Firebase...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-6">
                        <div className="flex items-center">
                            <Bot className="h-8 w-8 text-blue-600 mr-3" />
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Dashboard Administrativo</h1>
                                <p className="text-sm text-gray-500">Bot Financiero - Gestión y Analytics</p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                                <div className={`w-2 h-2 rounded-full ${state.realTimeConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                <span>
                                    {state.realTimeConnected ? 'Tiempo Real Activo' : 'Desconectado'} - {new Date().toLocaleTimeString('es-MX')}
                                </span>
                            </div>

                            <button
                                onClick={setupRealTimeListeners}
                                disabled={state.loading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                <RefreshCw className={`h-4 w-4 ${state.loading ? 'animate-spin' : ''}`} />
                                Reconectar
                            </button>

                            <button
                                onClick={() => {
                                    // Exportar datos como CSV
                                    const csvData = state.interactions.map(i => ({
                                        fecha: formatTime(i.timestamp),
                                        usuario: i.userId,
                                        mensaje: i.message,
                                        tipo: i.messageType
                                    }));

                                    const csv = [
                                        Object.keys(csvData[0]).join(','),
                                        ...csvData.map(row => Object.values(row).join(','))
                                    ].join('\n');

                                    const blob = new Blob([csv], { type: 'text/csv' });
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `dashboard-data-${new Date().toISOString().split('T')[0]}.csv`;
                                    a.click();
                                }}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                            >
                                <Download className="h-4 w-4" />
                                Exportar
                            </button>
                        </div>
                    </div>

                    {/* Navigation tabs */}
                    <nav className="flex space-x-8">
                        {[
                            { id: 'overview', name: 'Resumen', icon: Home },
                            { id: 'interactions', name: 'Conversaciones', icon: MessageSquare },
                            { id: 'users', name: 'Usuarios', icon: Users },
                            { id: 'expenses', name: 'Gastos', icon: DollarSign },
                            { id: 'analytics', name: 'Analytics', icon: TrendingUp }
                        ].map((tab) => {
                            const Icon = tab.icon;
                            const isActive = state.currentView === tab.id ||
                                (state.currentView === 'user-detail' && tab.id === 'users');

                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => dispatch({ type: 'SET_VIEW', payload: tab.id })}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${isActive
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    <Icon className="h-4 w-4" />
                                    {tab.name}
                                    {state.currentView === 'user-detail' && tab.id === 'users' && (
                                        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                                            Ver Usuario
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>

            <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                {state.currentView === 'overview' && renderOverview()}
                {state.currentView === 'interactions' && renderInteractions()}
                {state.currentView === 'users' && renderUsers()}
                {state.currentView === 'expenses' && renderExpenses()}
                {state.currentView === 'analytics' && renderAnalytics()}
                {state.currentView === 'user-detail' && state.selectedUser && (
                    <UserInfo
                        userId={state.selectedUser}
                        onBack={() => dispatch({ type: 'SET_VIEW', payload: 'users' })}
                        interactions={state.interactions}
                        responses={state.responses}
                        expenses={state.expenses}
                        reminders={state.reminders}
                        subscriptions={state.subscriptions}
                        users={state.users}
                    />
                )}
            </main>
        </div>
    );
};

export default AdminDashboard;